import { Toast } from '@base-ui/react';

import { SDSToastType } from './toast-types';

// Create a global toast manager instance
let globalToastManager: ReturnType<typeof Toast.createToastManager> | null = null;

export function initializeGlobalToastManager() {
  if (!globalToastManager) {
    globalToastManager = Toast.createToastManager();
  }
  return globalToastManager;
}

export function getGlobalToastManager() {
  return globalToastManager;
}

function showToast(message: string, type: SDSToastType) {
  const manager = getGlobalToastManager();
  if (!manager) {
    console.warn('Toast manager not initialized. Make sure SDSToastProvider is in your app.');
    return '';
  }

  return manager.add({
    title: message,
    data: { type },
  });
}

export interface ToastAPI {
  success: (message: string) => string;
  error: (message: string) => string;
  warning: (message: string) => string;
  info: (message: string) => string;
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    },
  ) => Promise<T>;
  dismiss: (id: string) => void;
}

export const toast: ToastAPI = {
  success: (message: string) => {
    return showToast(message, SDSToastType.Success);
  },

  error: (message: string) => {
    return showToast(message, SDSToastType.Error);
  },

  warning: (message: string) => {
    return showToast(message, SDSToastType.Warning);
  },

  info: (message: string) => {
    return showToast(message, SDSToastType.Info);
  },

  promise: async <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    },
  ): Promise<T> => {
    const manager = getGlobalToastManager();
    if (!manager) {
      console.warn('Toast manager not initialized. Make sure SDSToastProvider is in your app.');
      return promise;
    }

    const toastId = manager.add({
      title: messages.loading,
      data: { type: SDSToastType.Info },
    });

    try {
      const result = await promise;
      const successMessage = typeof messages.success === 'function' ? messages.success(result) : messages.success;
      manager.update(toastId, {
        title: successMessage,
        data: { type: SDSToastType.Success },
      });
      return result;
    } catch (error) {
      const errorMessage = typeof messages.error === 'function' ? messages.error(error as Error) : messages.error;
      manager.update(toastId, {
        title: errorMessage,
        data: { type: SDSToastType.Error },
      });
      throw error;
    }
  },

  dismiss: (id: string) => {
    const manager = getGlobalToastManager();
    if (!manager) {
      return;
    }
    manager.close(id);
  },
};
